// src/pages/forge/SquadMemberProfile.tsx - Using Supabase (No Hardcoded Data)
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Award,
  TrendingUp,
  Star,
  Target,
  Users,
  FileText,
  CheckCircle2,
  Clock,
  Upload,
  Download,
  MessageSquare,
  Edit,
  Save,
  X,
  Camera,
  CalendarPlus,
  Plus,
  Copy,
  Send,
  MoreVertical,
  Eye,
  ChevronLeft,
  Loader2,
  Trash2,
  MessageCircle,
  FolderOpen,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useSquadAuth } from '@/contexts/SquadAuthContext';
import { supabase } from '@/lib/supabaseClient';

// Helper functions
const getInitials = (name: string) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getRoleBadgeColor = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'market_analyst': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'product_lead': return 'bg-green-100 text-green-800 border-green-200';
    case 'finance_lead': return 'bg-orange-100 text-orange-800 border-orange-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getDisplayRole = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'Project Lead';
    case 'market_analyst': return 'Market Analyst';
    case 'product_lead': return 'Product Lead';
    case 'finance_lead': return 'Finance Lead';
    default: return role || 'Team Member';
  }
};

const getPriorityColor = (priority: string) => {
  switch(priority) {
    case 'high': return 'bg-red-100 text-red-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    case 'low': return 'bg-blue-100 text-blue-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getStatusBadge = (status: string) => {
  switch(status) {
    case 'approved': return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
    case 'reviewing': return <Badge className="bg-yellow-100 text-yellow-700">Reviewing</Badge>;
    case 'pending': return <Badge className="bg-orange-100 text-orange-700">Pending</Badge>;
    case 'rejected': return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
    case 'completed': return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
    default: return <Badge variant="outline">{status}</Badge>;
  }
};

interface Task {
  id: string;
  title: string;
  description: string;
  due_date: string;
  priority: string;
  status: string;
  created_by_name: string;
}

interface Meeting {
  id: string;
  title: string;
  description: string;
  meeting_date: string;
  meeting_time: string;
  type: string;
  agenda: string;
  meeting_link: string;
  status: string;
  created_by_name: string;
}

interface Document {
  id: string;
  name: string;
  description: string;
  category: string;
  file_name: string;
  file_type: string;
  file_size: string;
  file_url: string;
  status: string;
  uploaded_by_name: string;
  created_at: string;
}

interface DocumentComment {
  id: string;
  author_name: string;
  content: string;
  created_at: string;
}

const ANALYST_OPTIONS = [
  { id: 'analyst1', name: 'Dr. Sarah Kimani', role: 'Senior Analyst', type: 'analyst' },
  { id: 'analyst2', name: 'James Mwangi', role: 'Problem Bank Analyst', type: 'analyst' },
  { id: 'analyst3', name: 'Wanjiku Ndegwa', role: 'Business Analyst', type: 'analyst' },
  { id: 'analyst4', name: 'Michael Otieno', role: 'Market Analyst', type: 'analyst' },
  { id: 'analyst5', name: 'Grace Achieng', role: 'Financial Analyst', type: 'analyst' },
];

export default function SquadMemberProfile() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { squadUser } = useSquadAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [member, setMember] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  
  // Data from Supabase
  const [tasks, setTasks] = useState<Task[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [comments, setComments] = useState<Record<string, DocumentComment[]>>({});
  const [memberKPIs, setMemberKPIs] = useState<any[]>([]);
  const [squadMembers, setSquadMembers] = useState<any[]>([]);
  
  // UI States
  const [isEditing, setIsEditing] = useState(false);
  const [editedBio, setEditedBio] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedLocation, setEditedLocation] = useState('');
  
  // Dialog states
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [showMeetingDetailsDialog, setShowMeetingDetailsDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showDocumentUploadDialog, setShowDocumentUploadDialog] = useState(false);
  const [showDocumentCommentsDialog, setShowDocumentCommentsDialog] = useState(false);
  const [showAddCommentDialog, setShowAddCommentDialog] = useState(false);
  
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentAttachment, setCommentAttachment] = useState<File | null>(null);
  
  // Form states
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium'
  });
  
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    meeting_date: '',
    meeting_time: '',
    type: 'internal',
    agenda: '',
    meeting_link: '',
    attendees: [] as string[]
  });
  
  const [messageContent, setMessageContent] = useState('');
  
  const [newDocument, setNewDocument] = useState({
    name: '',
    description: '',
    category: 'other' as 'proposal' | 'research' | 'financial' | 'legal' | 'other',
    file: null as File | null
  });

  // Check if this is the current user's own profile using email
  const isOwnProfile = squadUser?.memberEmail === member?.email;

  // Load all data
  useEffect(() => {
    if (memberId) {
      loadAllData();
    }
  }, [memberId]);

  const loadAllData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load member from database
      const { data: memberData, error: memberError } = await supabase
        .from('squad_members')
        .select('*')
        .eq('id', memberId)
        .single();

      if (memberError) throw memberError;
      if (!memberData) throw new Error('Member not found');

      setMember(memberData);
      setEditedBio(memberData.bio || '');
      setEditedPhone(memberData.phone || '');
      setEditedLocation(memberData.location || '');

      // Load all squad members for attendee selection
      const { data: squadData } = await supabase
        .from('squad_members')
        .select('*')
        .eq('squad_id', memberData.squad_id);
      
      if (squadData) setSquadMembers(squadData);

      // Load tasks
      const { data: tasksData } = await supabase
        .from('squad_tasks')
        .select('*')
        .eq('squad_member_id', memberId)
        .order('due_date', { ascending: true });
      
      if (tasksData) setTasks(tasksData);

      // Load meetings
      const { data: meetingsData } = await supabase
        .from('squad_meetings')
        .select('*')
        .eq('squad_member_id', memberId)
        .order('meeting_date', { ascending: true });
      
      if (meetingsData) setMeetings(meetingsData);

      // Load documents
      const { data: docsData } = await supabase
        .from('squad_documents')
        .select('*')
        .eq('squad_member_id', memberId)
        .order('created_at', { ascending: false });
      
      if (docsData) {
        setDocuments(docsData);
        // Load comments for all documents
        const commentIds = docsData.map(d => d.id);
        if (commentIds.length > 0) {
          const { data: commentsData } = await supabase
            .from('document_comments')
            .select('*')
            .in('document_id', commentIds);
          
          if (commentsData) {
            const commentsByDoc: Record<string, DocumentComment[]> = {};
            commentsData.forEach((comment: any) => {
              if (!commentsByDoc[comment.document_id]) commentsByDoc[comment.document_id] = [];
              commentsByDoc[comment.document_id].push(comment);
            });
            setComments(commentsByDoc);
          }
        }
      }

      // Load KPIs
      const { data: kpisData } = await supabase
        .from('squad_member_kpis')
        .select('*, squad_kpi_definitions(*)')
        .eq('squad_member_id', memberId);
      
      if (kpisData) setMemberKPIs(kpisData);

    } catch (err: any) {
      console.error('Error loading member:', err);
      setError(err.message || 'Failed to load member profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!member) return;
    
    try {
      const { error: updateError } = await supabase
        .from('squad_members')
        .update({
          bio: editedBio,
          phone: editedPhone,
          location: editedLocation,
          updated_at: new Date().toISOString()
        })
        .eq('id', member.id);

      if (updateError) throw updateError;

      setMember({ ...member, bio: editedBio, phone: editedPhone, location: editedLocation });
      setIsEditing(false);
      toast({ title: "Profile updated", description: "Profile information has been saved." });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({ title: "Error", description: "Failed to save profile changes.", variant: "destructive" });
    }
  };

  const handleCancelEdit = () => {
    setEditedBio(member?.bio || '');
    setEditedPhone(member?.phone || '');
    setEditedLocation(member?.location || '');
    setIsEditing(false);
  };

  const handleCopyEmail = () => {
    if (member?.email) {
      navigator.clipboard.writeText(member.email);
      toast({ title: "Email copied", description: "Email address copied to clipboard." });
    }
  };

  // handleImageUpload function
  const handleImageUpload = () => {
    console.log('Image upload feature coming soon');
    toast({
      title: "Coming Soon",
      description: "Profile picture upload will be available in a future update.",
    });
  };

  const handleAddTask = async () => {
    if (!newTask.title || !newTask.due_date || !member) {
      toast({ title: "Missing information", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const { data, error } = await supabase
        .from('squad_tasks')
        .insert({
          squad_member_id: member.id,
          title: newTask.title,
          description: newTask.description,
          due_date: newTask.due_date,
          priority: newTask.priority,
          status: 'pending',
          created_by: squadUser?.memberId,
          created_by_name: squadUser?.memberName
        })
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => [...prev, data]);
      setShowTaskDialog(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium' });
      toast({ title: "Task added", description: "New task has been created." });
    } catch (error) {
      console.error('Error adding task:', error);
      toast({ title: "Error", description: "Failed to add task.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    
    try {
      const { error } = await supabase
        .from('squad_tasks')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev => prev.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      ));

      toast({ title: newStatus === 'completed' ? "Task completed" : "Task reopened" });
    } catch (error) {
      console.error('Error updating task:', error);
      toast({ title: "Error", description: "Failed to update task.", variant: "destructive" });
    }
  };

  const handleScheduleMeeting = async () => {
    if (!newMeeting.title || !newMeeting.meeting_date || !newMeeting.meeting_time || !member) {
      toast({ title: "Missing information", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const { data, error } = await supabase
        .from('squad_meetings')
        .insert({
          squad_member_id: member.id,
          title: newMeeting.title,
          description: newMeeting.description,
          meeting_date: newMeeting.meeting_date,
          meeting_time: newMeeting.meeting_time,
          type: newMeeting.type,
          agenda: newMeeting.agenda,
          meeting_link: newMeeting.meeting_link,
          status: 'upcoming',
          created_by: squadUser?.memberId,
          created_by_name: squadUser?.memberName
        })
        .select()
        .single();

      if (error) throw error;

      // Add attendees if any
      if (newMeeting.attendees.length > 0) {
        const attendeeInserts = newMeeting.attendees.map(attendeeId => {
          const isAnalyst = attendeeId.startsWith('analyst');
          const attendeeInfo = isAnalyst 
            ? ANALYST_OPTIONS.find(a => a.id === attendeeId)
            : squadMembers.find(m => m.id === attendeeId);
          
          return {
            meeting_id: data.id,
            attendee_id: attendeeId,
            attendee_name: attendeeInfo?.full_name || attendeeInfo?.name || attendeeId,
            attendee_role: attendeeInfo?.role || 'Team Member',
            attendee_type: isAnalyst ? 'analyst' : 'squad',
            rsvp_status: 'pending'
          };
        });

        await supabase.from('meeting_attendees').insert(attendeeInserts);
      }

      setMeetings(prev => [...prev, data]);
      setShowMeetingDialog(false);
      setNewMeeting({ title: '', description: '', meeting_date: '', meeting_time: '', type: 'internal', agenda: '', meeting_link: '', attendees: [] });
      toast({ title: "Meeting scheduled", description: "The meeting has been added to the calendar." });
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({ title: "Error", description: "Failed to schedule meeting.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!newDocument.name || !newDocument.file || !member) {
      toast({ title: "Missing information", description: "Please provide document name and file.", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = newDocument.file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `squad-documents/${member.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, newDocument.file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      const { data: docData, error: docError } = await supabase
        .from('squad_documents')
        .insert({
          squad_member_id: member.id,
          name: newDocument.name,
          description: newDocument.description,
          category: newDocument.category,
          file_url: urlData.publicUrl,
          file_name: newDocument.file.name,
          file_type: newDocument.file.type,
          file_size: `${(newDocument.file.size / 1024 / 1024).toFixed(1)} MB`,
          status: 'pending',
          uploaded_by: squadUser?.memberId,
          uploaded_by_name: squadUser?.memberName
        })
        .select()
        .single();

      if (docError) throw docError;

      setDocuments(prev => [docData, ...prev]);
      setShowDocumentUploadDialog(false);
      setNewDocument({ name: '', description: '', category: 'other', file: null });
      toast({ title: "Document uploaded", description: `${newDocument.name} has been uploaded.` });
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({ title: "Error", description: "Failed to upload document.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = (doc: Document) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    } else {
      const content = `Document: ${doc.name}\nDescription: ${doc.description}\nCategory: ${doc.category}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.name}.txt`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
    toast({ title: "Download started", description: `${doc.name} is being downloaded.` });
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedDocument) return;

    setIsUploading(true);
    try {
      const { data, error } = await supabase
        .from('document_comments')
        .insert({
          document_id: selectedDocument.id,
          author_id: squadUser?.memberId,
          author_name: squadUser?.memberName,
          content: commentText
        })
        .select()
        .single();

      if (error) throw error;

      setComments(prev => ({
        ...prev,
        [selectedDocument.id]: [...(prev[selectedDocument.id] || []), data]
      }));

      setCommentText('');
      setShowAddCommentDialog(false);
      toast({ title: "Comment added", description: "Your comment has been added." });
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({ title: "Error", description: "Failed to add comment.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateDocumentStatus = async (docId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('squad_documents')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', docId);

      if (error) throw error;

      setDocuments(prev => prev.map(doc =>
        doc.id === docId ? { ...doc, status: newStatus } : doc
      ));

      toast({ title: "Status updated", description: `Document status changed to ${newStatus}.` });
    } catch (error) {
      console.error('Error updating document status:', error);
      toast({ title: "Error", description: "Failed to update document status.", variant: "destructive" });
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      const { error } = await supabase
        .from('squad_documents')
        .delete()
        .eq('id', docId);

      if (error) throw error;

      setDocuments(prev => prev.filter(doc => doc.id !== docId));
      toast({ title: "Document deleted", description: "The document has been removed." });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({ title: "Error", description: "Failed to delete document.", variant: "destructive" });
    }
  };

  const handleSendMessage = () => {
    if (!messageContent.trim() || !member) {
      toast({ title: "Message is empty", description: "Please enter a message to send.", variant: "destructive" });
      return;
    }
    
    // Prevent self-messaging
    if (member.email === squadUser?.memberEmail) {
      toast({ title: "Cannot message self", description: "You cannot send a message to yourself.", variant: "destructive" });
      setShowMessageDialog(false);
      return;
    }
    
    toast({ title: "Message sent", description: `Your message to ${member.full_name} has been sent.` });
    setShowMessageDialog(false);
    setMessageContent('');
  };

  const completedKPIs = memberKPIs.filter(k => k.status === 'completed' || k.status === 'approved').length;
  const totalKPIs = memberKPIs.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const pendingTasks = tasks.filter(t => t.status !== 'completed').length;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">{error || 'Team member not found.'}</p>
          <Button onClick={() => navigate('/forge/squad')}>Back to Squad</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/forge/squad')} 
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Squad
          </Button>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {getDisplayRole(member.role)}
            </Badge>
            <span className="text-sm text-muted-foreground">Profile</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-6 py-6">
        {/* Profile Header Card */}
        <Card className="overflow-hidden mb-6">
          <div className="h-32 bg-gradient-to-r from-blue-600 to-purple-600" />
          <CardContent className="relative pt-0">
            <div className="flex flex-col md:flex-row gap-6 -mt-16">
              {/* Avatar */}
              <div className="flex-shrink-0 relative group">
                <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-white shadow-xl bg-white flex items-center justify-center">
                  {member.imageUrl && !imageError ? (
                    <img 
                      src={member.imageUrl}
                      alt={member.full_name}
                      className="h-full w-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className={`h-full w-full flex items-center justify-center text-3xl font-bold ${getRoleBadgeColor(member.role)}`}>
                      {getInitials(member.full_name)}
                    </div>
                  )}
                </div>
                {isOwnProfile && (
                  <label 
                    htmlFor="profile-photo" 
                    className="absolute bottom-0 right-0 bg-primary text-white p-2 rounded-full cursor-pointer hover:bg-primary/80 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
                  >
                    <Camera className="h-4 w-4" />
                  </label>
                )}
                <input
                  type="file"
                  id="profile-photo"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
              
              <div className="flex-1 pt-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-3xl font-bold">{member.full_name}</h1>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge className={getRoleBadgeColor(member.role)}>
                        {getDisplayRole(member.role)}
                      </Badge>
                      {isOwnProfile && <Badge variant="outline" className="bg-primary/10">You</Badge>}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {isEditing ? (
                      <>
                        <Button size="sm" onClick={handleSaveProfile}>
                          <Save className="h-4 w-4 mr-2" /> Save
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                          <X className="h-4 w-4 mr-2" /> Cancel
                        </Button>
                      </>
                    ) : (
                      isOwnProfile && (
                        <Button size="sm" onClick={() => setIsEditing(true)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit Profile
                        </Button>
                      )
                    )}
                    {!isOwnProfile && (
                      <Button variant="outline" size="sm" onClick={() => setShowMessageDialog(true)}>
                        <MessageSquare className="h-4 w-4 mr-2" /> Message
                      </Button>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        value={editedBio}
                        onChange={(e) => setEditedBio(e.target.value)}
                        rows={3}
                        placeholder="Tell us about yourself..."
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          value={editedPhone}
                          onChange={(e) => setEditedPhone(e.target.value)}
                          placeholder="Phone number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          value={editedLocation}
                          onChange={(e) => setEditedLocation(e.target.value)}
                          placeholder="City, Country"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="mt-4 text-muted-foreground">{member.bio || 'No bio provided yet.'}</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <div className="flex items-center gap-1">
                        <p className="font-medium">{member.email}</p>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={handleCopyEmail}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {member.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="font-medium">{member.phone}</p>
                      </div>
                    </div>
                  )}
                  {member.location && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div>
                        <p className="text-sm text-muted-foreground">Location</p>
                        <p className="font-medium">{member.location}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks">Tasks ({pendingTasks})</TabsTrigger>
            <TabsTrigger value="meetings">Meetings</TabsTrigger>
            <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    Education
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-line">{member.education || 'Not specified'}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5" />
                    Specializations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {member.specialization?.map((spec: string, i: number) => (
                      <Badge key={i} variant="secondary">{spec}</Badge>
                    )) || <p className="text-muted-foreground">None specified</p>}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Key Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <p className="text-2xl font-bold">{completedKPIs}/{totalKPIs}</p>
                    <p className="text-sm text-muted-foreground">KPIs Done</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <p className="text-2xl font-bold">{completedTasks}/{tasks.length}</p>
                    <p className="text-sm text-muted-foreground">Tasks Done</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <p className="text-2xl font-bold">{meetings.length}</p>
                    <p className="text-sm text-muted-foreground">Meetings</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <p className="text-2xl font-bold">{documents.length}</p>
                    <p className="text-sm text-muted-foreground">Documents</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Assessment Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  {member.assessment_completed ? (
                    <>
                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                      <div>
                        <p className="font-medium text-green-700">Assessment Completed</p>
                        <p className="text-sm text-muted-foreground">This member has completed their squad assessment.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Clock className="h-6 w-6 text-yellow-500" />
                      <div>
                        <p className="font-medium text-yellow-700">Assessment Pending</p>
                        <p className="text-sm text-muted-foreground">This member has not yet completed their assessment.</p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Tasks & To-Dos</CardTitle>
                {isOwnProfile && (
                  <Button size="sm" onClick={() => setShowTaskDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add Task
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {tasks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No tasks assigned</p>
                ) : (
                  <div className="space-y-4">
                    {tasks.map((task) => (
                      <div key={task.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-3">
                        <div className="flex items-start gap-3">
                          {isOwnProfile && (
                            <Checkbox 
                              checked={task.status === 'completed'}
                              onCheckedChange={() => handleToggleTask(task.id, task.status)}
                              className="mt-1"
                            />
                          )}
                          <div>
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-sm text-muted-foreground">{task.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge className={getPriorityColor(task.priority)}>
                          {task.priority}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meetings Tab */}
          <TabsContent value="meetings">
            <div className="space-y-6">
              {isOwnProfile && (
                <div className="flex justify-end">
                  <Button onClick={() => setShowMeetingDialog(true)}>
                    <CalendarPlus className="h-4 w-4 mr-2" /> Schedule Meeting
                  </Button>
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Upcoming Meetings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {meetings.filter(m => m.status === 'upcoming').length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No upcoming meetings</p>
                    ) : (
                      <div className="space-y-4">
                        {meetings.filter(m => m.status === 'upcoming').map((meeting) => (
                          <div key={meeting.id} className="p-4 border rounded-lg">
                            <h3 className="font-medium">{meeting.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {new Date(meeting.meeting_date).toLocaleDateString()} at {meeting.meeting_time}
                            </p>
                            {meeting.meeting_link && (
                              <Button variant="outline" size="sm" className="mt-2" onClick={() => window.open(meeting.meeting_link, '_blank')}>
                                <Video className="h-3 w-3 mr-1" /> Join
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Past Meetings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {meetings.filter(m => m.status === 'completed' || new Date(m.meeting_date) < new Date()).length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No past meetings</p>
                    ) : (
                      <div className="space-y-4">
                        {meetings.filter(m => m.status === 'completed' || new Date(m.meeting_date) < new Date()).map((meeting) => (
                          <div key={meeting.id} className="p-4 border rounded-lg">
                            <h3 className="font-medium">{meeting.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {new Date(meeting.meeting_date).toLocaleDateString()}
                            </p>
                            {meeting.agenda && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{meeting.agenda}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Documents</CardTitle>
                {isOwnProfile && (
                  <Button size="sm" onClick={() => setShowDocumentUploadDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" /> Upload
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No documents uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-primary" />
                            <div>
                              <p className="font-medium">{doc.name}</p>
                              <p className="text-sm text-muted-foreground">{doc.description || 'No description'}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="outline">{doc.category}</Badge>
                            <span className="text-xs text-muted-foreground">{doc.file_type}</span>
                            <span className="text-xs text-muted-foreground">{doc.file_size}</span>
                            {getStatusBadge(doc.status)}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadDocument(doc)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => {
                            setSelectedDocument(doc);
                            setShowDocumentCommentsDialog(true);
                          }}>
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                          {isOwnProfile && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  KPI Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center p-4 bg-muted/30 rounded-lg mb-6">
                  <p className="text-3xl font-bold">{completedKPIs}/{totalKPIs}</p>
                  <p className="text-sm text-muted-foreground">KPIs Completed</p>
                  <Progress value={totalKPIs > 0 ? (completedKPIs / totalKPIs) * 100 : 0} className="h-2 mt-2" />
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium">KPI Breakdown</h3>
                  {memberKPIs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No KPIs assigned yet.</p>
                  ) : (
                    memberKPIs.map((kpi) => (
                      <div key={kpi.id} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{kpi.squad_kpi_definitions?.kpi_name || 'KPI'}</span>
                          <Badge className={kpi.status === 'completed' || kpi.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {kpi.status || 'not_started'}
                          </Badge>
                        </div>
                        {kpi.status !== 'completed' && kpi.status !== 'approved' && (
                          <Progress value={0} className="h-2" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ========== DIALOGS ========== */}

      {/* Add Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>Create a new task assignment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Task Title *</Label>
              <Input
                placeholder="e.g., Complete market research"
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Additional details"
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={newTask.priority} onValueChange={(value) => setNewTask({...newTask, priority: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTask} disabled={isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Meeting Dialog */}
      <Dialog open={showMeetingDialog} onOpenChange={setShowMeetingDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Meeting</DialogTitle>
            <DialogDescription>Schedule a meeting with squad members and analysts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Meeting Title *</Label>
              <Input
                placeholder="e.g., Weekly Squad Sync"
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="What is this meeting about?"
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({...newMeeting, description: e.target.value})}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newMeeting.meeting_date}
                  onChange={(e) => setNewMeeting({...newMeeting, meeting_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={newMeeting.meeting_time}
                  onChange={(e) => setNewMeeting({...newMeeting, meeting_time: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Meeting Type</Label>
              <Select value={newMeeting.type} onValueChange={(value) => setNewMeeting({...newMeeting, type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal (Squad)</SelectItem>
                  <SelectItem value="analyst">Analyst Review</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="mentor">Mentor</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Invite Attendees</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                <p className="text-xs font-semibold">Squad Members</p>
                {squadMembers.map((m) => (
                  <div key={m.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={newMeeting.attendees.includes(m.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewMeeting({...newMeeting, attendees: [...newMeeting.attendees, m.id]});
                        } else {
                          setNewMeeting({...newMeeting, attendees: newMeeting.attendees.filter(id => id !== m.id)});
                        }
                      }}
                    />
                    <Label className="text-sm cursor-pointer">{m.full_name} ({getDisplayRole(m.role)})</Label>
                  </div>
                ))}
                <p className="text-xs font-semibold mt-3">Analysts</p>
                {ANALYST_OPTIONS.map((a) => (
                  <div key={a.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={newMeeting.attendees.includes(a.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewMeeting({...newMeeting, attendees: [...newMeeting.attendees, a.id]});
                        } else {
                          setNewMeeting({...newMeeting, attendees: newMeeting.attendees.filter(id => id !== a.id)});
                        }
                      }}
                    />
                    <Label className="text-sm cursor-pointer">{a.name} ({a.role})</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Agenda</Label>
              <Textarea
                placeholder="What will be covered?"
                value={newMeeting.agenda}
                onChange={(e) => setNewMeeting({...newMeeting, agenda: e.target.value})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Meeting Link (Optional)</Label>
              <Input
                placeholder="https://meet.google.com/..."
                value={newMeeting.meeting_link}
                onChange={(e) => setNewMeeting({...newMeeting, meeting_link: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMeetingDialog(false)}>Cancel</Button>
            <Button onClick={handleScheduleMeeting} disabled={isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarPlus className="h-4 w-4 mr-2" />}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Message to {member?.full_name}</DialogTitle>
            <DialogDescription>Send a direct message to this team member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Type your message here..."
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button onClick={handleSendMessage}><Send className="h-4 w-4 mr-2" /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={showDocumentUploadDialog} onOpenChange={setShowDocumentUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Add document details and upload file</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Title *</Label>
              <Input
                placeholder="e.g., Q1 Market Research Report"
                value={newDocument.name}
                onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="What is this document about?"
                value={newDocument.description}
                onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={newDocument.category} onValueChange={(value) => setNewDocument({...newDocument, category: value as any})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="research">Research</SelectItem>
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="legal">Legal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File *</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm">{newDocument.file ? newDocument.file.name : 'Click to select file'}</p>
                <input type="file" className="hidden" ref={fileInputRef} onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setNewDocument({...newDocument, file: e.target.files[0]});
                  }
                }} />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Browse Files
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleDocumentUpload} disabled={!newDocument.file || !newDocument.name || isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Comments Dialog */}
      <Dialog open={showDocumentCommentsDialog} onOpenChange={setShowDocumentCommentsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.name} - Comments</DialogTitle>
            <DialogDescription>{selectedDocument?.description}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b">
              {getStatusBadge(selectedDocument?.status || 'pending')}
              {isOwnProfile && selectedDocument && (
                <Select onValueChange={(value) => handleUpdateDocumentStatus(selectedDocument.id, value)}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue placeholder="Change status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {(comments[selectedDocument?.id || ''] || []).map((comment) => (
              <div key={comment.id} className="p-3 bg-muted/30 rounded-lg">
                <div className="flex justify-between">
                  <span className="font-medium text-sm">{comment.author_name}</span>
                  <span className="text-xs text-muted-foreground">{new Date(comment.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm mt-1">{comment.content}</p>
              </div>
            ))}
            
            {(!comments[selectedDocument?.id || ''] || comments[selectedDocument?.id || ''].length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No comments yet</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCommentDialog(true)}>
              <MessageCircle className="h-4 w-4 mr-2" /> Add Comment
            </Button>
            <Button variant="outline" onClick={() => setShowDocumentCommentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={showAddCommentDialog} onOpenChange={setShowAddCommentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Comment</DialogTitle>
            <DialogDescription>Share your feedback on this document</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Type your comment here..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCommentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddComment} disabled={!commentText.trim() || isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}